import { FC, useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Autocomplete, Box, MenuItem, Stack, TextField } from '@mui/material'

import { InputTranslator } from '../../../../components/InputTranslator/InputTranslator'
import { InternalFormDialog } from '../../../../components/InternalFormDialog/InternalFormDialog'
import {
  emptyKeyword,
  KeywordFormValues,
  keywordLanguages,
  keywordPriorities,
  keywordPriorityLabelKeys,
  toKeywordFormValues,
  toKeywordMutation,
} from '../../helpers/keywordHelpers'
import { KeywordFeature, KeywordMutation } from '../../types/InternalKeyword'

type KeywordEditDialogProps = {
  open: boolean
  keyword?: KeywordMutation
  features: KeywordFeature[]
  isSaving?: boolean
  onClose: () => void
  onSave: (keyword: KeywordMutation) => void
}

// No <form> here: the InputTranslator dialog has its own form, and React bubbles its submit through the portal into any parent form.
export const KeywordEditDialog: FC<KeywordEditDialogProps> = ({ open, keyword = emptyKeyword, features, isSaving, onClose, onSave }) => {
  const { t } = useTranslation()
  const { control, handleSubmit, reset, formState } = useForm<KeywordFormValues>({
    mode: 'onChange',
    defaultValues: toKeywordFormValues(keyword, features),
  })

  useEffect(() => {
    if (open) {
      reset(toKeywordFormValues(keyword, features))
    }
  }, [open, keyword, features, reset])

  const submit = handleSubmit((values) => onSave(toKeywordMutation(keyword, values, features)))

  return (
    <InternalFormDialog
      open={open}
      title={keyword.id ? t('internal-service-management:keywords_edit') : t('internal-service-management:keywords_new')}
      onClose={onClose}
      onSave={submit}
      saveDisabled={isSaving || !formState.isValid}
    >
      <Stack spacing={2} pt={1}>
        <Stack direction="row" spacing={2}>
          <Box flex={1}>
            <Controller
              name="names"
              control={control}
              rules={{ validate: (names) => !!names.en?.trim() }}
              render={({ field }) => (
                <InputTranslator
                  value={field.value}
                  onChange={field.onChange}
                  label={t('internal-service-management:keywords_name')}
                  dialogTitle={t('internal-service-management:keywords_name')}
                  selectedLanguages={keywordLanguages}
                />
              )}
            />
          </Box>
          <Controller
            name="priority"
            control={control}
            render={({ field }) => (
              <TextField {...field} select size="small" label={t('internal-service-management:keywords_priority')} sx={{ minWidth: 160 }}>
                {keywordPriorities.map((priority) => (
                  <MenuItem key={priority} value={priority}>
                    {t(keywordPriorityLabelKeys[priority])}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Stack>
        <Controller
          name="linkedFeatures"
          control={control}
          render={({ field }) => (
            <Autocomplete
              multiple
              filterSelectedOptions
              options={features}
              value={field.value}
              onChange={(_, value) => field.onChange(value)}
              getOptionLabel={(feature) => feature.name}
              isOptionEqualToValue={(option, value) => option.legacyId === value.legacyId}
              renderInput={(params) => <TextField {...params} label={t('internal-service-management:keywords_linked_features')} />}
            />
          )}
        />
      </Stack>
    </InternalFormDialog>
  )
}
